import { useEffect, useRef } from 'react';
import type { Invoice } from './types';
import { formatDateTime, formatDate, formatMoney, TIMEZONE_NOTE } from './dateFormat';
import { downloadInvoice } from './invoiceDocument';

/**
 * Invoice detail modal, opened from the billing-history table's "View" action.
 * Layered above the Settings modal (higher z-index via .billing-invoice-overlay)
 * and reuses the .settings-overlay token scope for theming.
 *
 * Focus management: on open we move focus to the Close button and restore it to
 * the previously-focused element on close; Escape and backdrop click both close.
 */

const STATUS_LABEL: Record<Invoice['status'], string> = {
  SUCCESS: 'Paid',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
};

interface InvoiceModalProps {
  /** The invoice to show, or null when closed. */
  invoice: Invoice | null;
  onClose(): void;
}

export function InvoiceModal({ invoice, onClose }: InvoiceModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = invoice != null;

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation(); // don't also close the Settings modal underneath
        onClose();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Move focus in on open, restore it on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [open]);

  if (!invoice) return null;

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Invoice date', value: formatDateTime(invoice.createdAt) },
    { label: 'Amount', value: formatMoney(invoice.amount, invoice.currency) },
    { label: 'Status', value: STATUS_LABEL[invoice.status] ?? invoice.status },
    {
      label: 'Billing period',
      value:
        invoice.billingPeriodStart || invoice.billingPeriodEnd
          ? `${formatDate(invoice.billingPeriodStart)} – ${formatDate(invoice.billingPeriodEnd)}`
          : '—',
    },
    { label: 'Order ID', value: invoice.paymentServiceOrderId || '—' },
    { label: 'Transaction ID', value: invoice.paymentTransactionId || '—' },
  ];
  if (invoice.status !== 'SUCCESS' && invoice.failureReason) {
    rows.push({ label: 'Reason', value: invoice.failureReason });
  }
  if (invoice.gatewayTransactionId) {
    rows.push({ label: 'Payment reference', value: invoice.gatewayTransactionId });
  }
  if (invoice.gateway) {
    rows.push({ label: 'Gateway', value: invoice.gateway });
  }

  return (
    <div className="settings-overlay billing-invoice-overlay" onMouseDown={onClose}>
      <div
        className="settings-modal billing-invoice"
        role="dialog"
        aria-modal="true"
        aria-label={`Invoice ${formatDate(invoice.createdAt)}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="settings-modal__head">
          <h2 className="settings-modal__title">Invoice</h2>
          <button
            type="button"
            ref={closeRef}
            className="settings-modal__close"
            aria-label="Close invoice"
            title="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="settings-body">
          <div className="billing-invoice__amount">{formatMoney(invoice.amount, invoice.currency)}</div>
          <dl className="billing-invoice__rows">
            {rows.map((r) => (
              <div className="billing-invoice__row" key={r.label}>
                <dt className="billing-invoice__label">{r.label}</dt>
                <dd className="billing-invoice__value">{r.value}</dd>
              </div>
            ))}
          </dl>
          <div className="billing-invoice__actions">
            <button
              type="button"
              className="billing-btn billing-btn--primary"
              onClick={() => downloadInvoice(invoice)}
            >
              Download invoice
            </button>
          </div>
          <p className="billing-invoice__note">{TIMEZONE_NOTE}. Opens a print-ready invoice — choose “Save as PDF”.</p>
        </div>
      </div>
    </div>
  );
}
